import React from "react";
import { Image, Text, View, Page, Document } from "@react-pdf/renderer";
import logo from "../../assets/BayRock_Labs_Black_JPG.jpg";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// Add UTC plugin
dayjs.extend(utc);

const formatDate = (dateString) => {
  if (!dateString) return "--";

  // Parse the date in UTC to avoid timezone issues
  const date = dayjs.utc(dateString);

  // Format the date in a consistent way regardless of timezone
  const day = date.date();
  const month = date.format("MMM");
  const year = date.year();

  return `${day} ${month} ${year}`;
};

const Invoice = ({ row }) => {
  // Create a simple data object from the API response
  const invoiceData = {
    invoiceNumber: row?.c2c_invoice_id || "--",
    invoiceDate: formatDate(row?.c2c_invoice_generated_on),
    dueDate: formatDate(row?.invoice_due_date),
    projectName: row?.c2c_invoice_contract_name || "--",
    poNumber: row?.purchase_order_number || "--",
    clientName: row?.client_name || "--",
    clientAddress: row?.invoice_client_address || "--",
    qty: row?.invoice_quantity ?? row?.c2c_total_hours_count ?? "--",
    rate: row?.invoice_rate ?? "--",
    serviced: row?.hasOwnProperty("invoice_services")
      ? row.invoice_services
        ? "Yes"
        : "No"
      : "--",

    amount: parseFloat(row?.c2c_invoice_amount || "0.00"),
    phoneNumber: "--",
    terms: row?.invoice_terms || "--",
    paymentsCredits: row?.invoice_payments_credits ?? "--"
  };

  return (
    <Document>
      <Page size="A4" style={{ padding: 30, fontSize: 12 }}>
        {/* Header */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: 40
          }}
        >
          <View>
            <Text style={{ fontSize: 11, fontWeight: "bold", marginBottom: 5 }}>
              BAYROCK LABS INC
            </Text>
            <Text style={{ fontSize: 10 }}>1201 N Orange St., Suite 750</Text>
            <Text style={{ fontSize: 10 }}>DE 19801</Text>
          </View>
          <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
            <Image
              style={{ width: 150, height: 40, objectFit: "contain" }}
              src={logo}
            />
            <Text
              style={{ fontSize: 24, fontWeight: "bold", marginBottom: 10 }}
            >
              Invoice
            </Text>
          </View>
        </View>

        {/* Invoice Info */}
        <View
          style={{
            flexDirection: "row",
            justifyContent: "flex-end",
            marginBottom: 20
          }}
        >
          <View style={{ width: 200, borderStyle: "solid", borderWidth: 1 }}>
            <View
              style={{
                flexDirection: "row",
                borderBottomWidth: 1,
                borderColor: "#000"
              }}
            >
              <View
                style={{
                  padding: 5,
                  width: "50%",
                  borderRightWidth: 1,
                  borderColor: "#000"
                }}
              >
                <Text style={{ fontSize: 11 }}>Date</Text>
              </View>
              <View style={{ padding: 5, width: "50%" }}>
                <Text style={{ fontSize: 11 }}>Invoice #</Text>
              </View>
            </View>
            <View style={{ flexDirection: "row" }}>
              <View
                style={{
                  padding: 5,
                  width: "50%",
                  borderRightWidth: 1,
                  borderColor: "#000"
                }}
              >
                <Text style={{ fontSize: 10 }}>{invoiceData.invoiceDate}</Text>
              </View>
              <View style={{ padding: 5, width: "50%" }}>
                <Text style={{ fontSize: 10 }}>
                  {invoiceData.invoiceNumber}
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Bill To Section */}
        <View
          style={{
            marginBottom: 5,
            borderStyle: "solid",
            borderWidth: 1,
            padding: 3,
            width: 270
          }}
        >
          <Text style={{ marginBottom: 5, fontWeight: "bold", fontSize: 11 }}>
            Bill To: {invoiceData.clientName}
          </Text>
        </View>

        <View
          style={{
            borderStyle: "solid",
            borderWidth: 1,
            padding: 3,
            width: 270,
            marginBottom: 15
          }}
        >
          <Text>
            <Text style={{ fontSize: 11, fontWeight: "bold" }}>
              Client Name:{" "}
            </Text>
            <Text style={{ fontSize: 10 }}>{invoiceData.clientName}</Text>
          </Text>
          <Text>
            <Text style={{ fontSize: 11, fontWeight: "bold" }}>Address: </Text>
            <Text style={{ fontSize: 10 }}>{invoiceData.clientAddress}</Text>
          </Text>
        </View>

        {/* Terms Table */}
        <View
          style={{
            width: "100%",
            marginBottom: 10,
            borderStyle: "solid",
            borderWidth: 1
          }}
        >
          <View
            style={{
              flexDirection: "row",
              borderBottomWidth: 1,
              borderBottomStyle: "solid"
            }}
          >
            <View
              style={{
                padding: 5,
                width: "33.33%",
                borderRightWidth: 1,
                borderRightStyle: "solid"
              }}
            >
              <Text style={{ fontSize: 11 }}>P.O. No.</Text>
            </View>
            <View
              style={{
                padding: 5,
                width: "33.33%",
                borderRightWidth: 1,
                borderRightStyle: "solid"
              }}
            >
              <Text style={{ fontSize: 11 }}>Terms</Text>
            </View>
            <View style={{ padding: 5, width: "33.33%" }}>
              <Text style={{ fontSize: 11 }}>Due Date</Text>
            </View>
          </View>

          <View style={{ flexDirection: "row" }}>
            <View
              style={{
                padding: 5,
                width: "33.33%",
                borderRightWidth: 1,
                borderRightStyle: "solid"
              }}
            >
              <Text style={{ fontSize: 10 }}>{invoiceData.poNumber}</Text>
            </View>
            <View
              style={{
                padding: 5,
                width: "33.33%",
                borderRightWidth: 1,
                borderRightStyle: "solid"
              }}
            >
              <Text style={{ fontSize: 10 }}>{invoiceData.terms}</Text>
            </View>
            <View style={{ padding: 5, width: "33.33%" }}>
              <Text style={{ fontSize: 10 }}>{invoiceData.dueDate}</Text>
            </View>
          </View>
        </View>

        {/* Table */}
        <View style={{ marginBottom: 0, borderWidth: 1, borderStyle: "solid" }}>
          {/* Table Header */}
          <View
            style={{
              flexDirection: "row",
              borderBottomWidth: 1,
              borderBottomStyle: "solid"
            }}
          >
            <View
              style={{
                padding: 8,
                width: "40%",
                borderRightWidth: 1,
                borderRightStyle: "solid",
                backgroundColor: "#d3d3d3"
              }}
            >
              <Text style={{ fontSize: 11 }}>Description</Text>
            </View>
            <View
              style={{
                padding: 8,
                width: "15%",
                borderRightWidth: 1,
                borderRightStyle: "solid",
                backgroundColor: "#d3d3d3"
              }}
            >
              <Text style={{ fontSize: 11 }}>Qty</Text>
            </View>
            <View
              style={{
                padding: 8,
                width: "15%",
                borderRightWidth: 1,
                borderRightStyle: "solid",
                backgroundColor: "#d3d3d3"
              }}
            >
              <Text style={{ fontSize: 11 }}>Rate</Text>
            </View>
            <View
              style={{
                padding: 8,
                width: "15%",
                borderRightWidth: 1,
                borderRightStyle: "solid",
                backgroundColor: "#d3d3d3"
              }}
            >
              <Text style={{ fontSize: 11 }}>Serviced</Text>
            </View>
            <View
              style={{
                padding: 8,
                width: "15%",
                backgroundColor: "#d3d3d3"
              }}
            >
              <Text style={{ fontSize: 11 }}>Amount</Text>
            </View>
          </View>

          {/* Table Rows */}
          <View style={{ flexDirection: "row" }}>
            <View style={{ padding: 8, width: "40%" }}>
              <Text style={{ fontSize: 10 }}>{invoiceData.projectName}</Text>
            </View>
            <View style={{ padding: 8, width: "15%" }}>
              <Text style={{ fontSize: 10 }}>{invoiceData.qty}</Text>
            </View>
            <View style={{ padding: 8, width: "15%" }}>
              <Text style={{ fontSize: 10 }}>{invoiceData.rate}</Text>
            </View>
            <View style={{ padding: 8, width: "15%" }}>
              <Text style={{ fontSize: 10 }}>{invoiceData.serviced}</Text>
            </View>
            <View style={{ padding: 8, width: "15%" }}>
              <Text style={{ fontSize: 10 }}>
                ${invoiceData.amount.toFixed(2)}
              </Text>
            </View>
          </View>

          {/* Thank you message */}
          <View
            style={{
              flexDirection: "row",
              borderTopWidth: 1,
              borderTopStyle: "solid"
            }}
          >
            <View
              style={{
                padding: 8,
                width: "100%"
              }}
            >
              <Text style={{ fontSize: 10 }}>Thank you for your business.</Text>
            </View>
          </View>
        </View>

        {/* Footer */}
        <View
          style={{
            marginTop: 30,
            flexDirection: "row",
            justifyContent: "space-between"
          }}
        >
          {/* Left side - ACH Information */}
          <View
            style={{
              width: "60%",
              borderWidth: 1,
              borderStyle: "solid",
              padding: 5
            }}
          >
            <Text style={{ fontSize: 11, marginBottom: 5 }}>
              Please find below our ACH information for your reference:
            </Text>
            <Text style={{ fontSize: 10, marginBottom: 2 }}>
              Account Number: 8576066280
            </Text>
            <Text style={{ fontSize: 10, marginBottom: 2 }}>
              ACH routing number: 121042882
            </Text>
            <Text style={{ fontSize: 10, marginBottom: 2 }}>
              Wire routing number: 121000248
            </Text>
            <Text style={{ fontSize: 10, marginBottom: 2 }}>
              SWIFT/BIC code: WFBIUS6S
            </Text>
            <Text style={{ fontSize: 10 }}>
              Bank name and address: Wells Fargo Bank, 420 Montgomery Street,
              San Francisco, CA 94104
            </Text>
          </View>

          {/* Right side - Totals */}
          <View style={{ width: "35%" }}>
            <View
              style={{
                flexDirection: "row",
                borderWidth: 1,
                borderStyle: "solid"
              }}
            >
              <View
                style={{
                  padding: 5,
                  width: "60%",
                  borderRightWidth: 1,
                  borderRightStyle: "solid"
                }}
              >
                <Text style={{ fontSize: 11 }}>Total</Text>
              </View>
              <View style={{ padding: 5, width: "40%" }}>
                <Text style={{ fontSize: 10 }}>
                  ${invoiceData.amount.toFixed(2)}
                </Text>
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
                borderWidth: 1,
                borderStyle: "solid"
              }}
            >
              <View
                style={{
                  padding: 5,
                  width: "60%",
                  borderRightWidth: 1,
                  borderRightStyle: "solid"
                }}
              >
                <Text style={{ fontSize: 11 }}>Payments/Credits</Text>
              </View>
              <View style={{ padding: 5, width: "40%" }}>
                <Text style={{ fontSize: 10 }}>
                  {invoiceData.paymentsCredits}
                </Text>
              </View>
            </View>
            <View
              style={{
                flexDirection: "row",
                borderWidth: 1,
                borderStyle: "solid",
                marginTop: -1
              }}
            >
              <View
                style={{
                  padding: 5,
                  width: "60%",
                  borderRightWidth: 1,
                  borderRightStyle: "solid"
                }}
              >
                <Text style={{ fontSize: 11 }}>Balance Due</Text>
              </View>
              <View style={{ padding: 5, width: "40%" }}>
                <Text style={{ fontSize: 10 }}>
                  ${invoiceData.amount.toFixed(2)}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default Invoice;
